import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { findNavItemForScreen, isDeepScreen, showsArtistPill, SMLNavGroupId } from '../smlNavigation';
import { SMLNavBadges } from '../hooks/useNavBadges';
import { useCurrentNavScreen } from '../hooks/useCurrentNavScreen';
import { useVisibleNavGroups } from '../hooks/useVisibleNavGroups';
import { ArtistSwitcherSheet } from '../../../accountSelector/ArtistSwitcherSheet';
import { MobileTopBar } from './MobileTopBar';
import { MobileDeepTopBar } from './MobileDeepTopBar';
import { MobileBottomBar } from './MobileBottomBar';
import { MobileGroupSheet } from './MobileGroupSheet';
import { MobileAccountSheet } from './MobileAccountSheet';
import { AndroidAppBanner, useAndroidAppBannerShown } from '../../../mobileApps/AndroidAppBanner';
import styles from './MobileNav.module.scss';

type OpenSheet = SMLNavGroupId | 'account' | 'artist' | undefined;

const SHEET_ID = 'm24-mobile-nav-sheet';

interface MobileNavProps {
  badges: SMLNavBadges;
}

/**
 * The mobile web navigation, 768 px and down (design navigation-v2 7a–7g): a top bar at the head
 * of the page and a fixed bottom bar with Home and the groups (5 tabs). A group
 * tap opens its hub sheet; the avatar opens the account sheet; an artist pill (every page but
 * Discover) opens the switcher sheet; search opens the command palette full screen. The open sheet is UI
 * state, not a route, so the URL stays the page's own. Above 768 px the CSS hides the bars and the
 * sidebar takes over.
 */
const MobileNavComponent: React.FC<MobileNavProps> = ({ badges }) => {
  const { screen, group: currentGroup } = useCurrentNavScreen();
  const { selectedDateValue } = useSMLDatePickerDateParam();
  const params = useMemo(() => ({ date: selectedDateValue }), [selectedDateValue]);
  const [openSheet, setOpenSheet] = useState<OpenSheet>(undefined);
  const [isAppBannerShown, dismissAppBanner] = useAndroidAppBannerShown();

  // Owner 2026-10-26: the artist pill on every page but a Discover page.
  const showArtistPill = showsArtistPill(currentGroup);

  const close = useCallback(() => setOpenSheet(undefined), []);
  const openAccount = useCallback(() => setOpenSheet('account'), []);
  const openArtist = useCallback(() => setOpenSheet('artist'), []);
  // design: navigation i1. Both artist pills open the switcher as a sheet, not as a dropdown.
  const artistSheet = useMemo(
    () => ({ id: SHEET_ID, isOpen: openSheet === 'artist', onOpen: openArtist }),
    [openSheet, openArtist]
  );
  const toggleGroup = useCallback(
    (groupId: SMLNavGroupId) => setOpenSheet((current) => (current === groupId ? undefined : groupId)),
    []
  );

  // A link that opens another page closes whatever sheet was open.
  useEffect(() => {
    setOpenSheet(undefined);
  }, [screen]);

  const navGroups = useVisibleNavGroups();
  const openGroup = useMemo(() => navGroups.find((group) => group.id === openSheet), [navGroups, openSheet]);
  // Design b4: a page below a destination gets back + title instead of the standard bar.
  const deepParent = useMemo(() => {
    const item = findNavItemForScreen(screen);
    return item !== undefined && isDeepScreen(item, screen) ? item : undefined;
  }, [screen]);

  return (
    <>
      <div className={styles.topSlot}>
        {isAppBannerShown && <AndroidAppBanner onDismiss={dismissAppBanner} />}
        {deepParent !== undefined ? (
          <MobileDeepTopBar parent={deepParent} artistSheet={showArtistPill ? artistSheet : undefined} />
        ) : (
          <MobileTopBar
            screen={screen}
            isAccountOpen={openSheet === 'account'}
            accountSheetId={SHEET_ID}
            onOpenAccount={openAccount}
            artistSheet={showArtistPill ? artistSheet : undefined}
            accountAttention={badges.accountAttention}
            params={params}
          />
        )}
      </div>
      <div className={clsx(styles.topSpacer, isAppBannerShown && styles.topSpacerWithBanner)} aria-hidden="true" />
      <div className={styles.bottomSlot}>
        {openGroup !== undefined && (
          <MobileGroupSheet
            id={SHEET_ID}
            group={openGroup}
            screen={screen}
            badges={badges}
            params={params}
            onClose={close}
          />
        )}
        {openSheet === 'account' && (
          <MobileAccountSheet id={SHEET_ID} accountAttention={badges.accountAttention} onClose={close} />
        )}
        {openSheet === 'artist' && <ArtistSwitcherSheet id={SHEET_ID} onClose={close} />}
        <MobileBottomBar
          screen={screen}
          currentGroup={currentGroup}
          openGroupId={openGroup?.id}
          sheetId={SHEET_ID}
          badges={badges}
          params={params}
          onToggleGroup={toggleGroup}
          onNavigateHome={close}
        />
      </div>
    </>
  );
};

export const MobileNav = TypedMemo(MobileNavComponent);
